import { css } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { whatsapp } from "../../assets";
import { iDialogMessagesFile } from "../use-omnichannel";
import Avatar from "../avatar";
import { ImageViewer } from "../chat/image-viewer";

export interface TopBarChatInfosProps {
  dialog: iDialogMessagesFile;
  contactName?: string | null;
  avatarUrl?: string | null;
  avatarLoading?: boolean;
  onLoadWhatsappAvatar: () => void;
  onAvatarUpload: (file?: File) => void;
  onRemoveAvatar: () => void;
}

export interface ContactAvatarActionProps {
  name?: string | null;
  avatarUrl?: string | null;
  avatarLoading?: boolean;
  onLoadWhatsappAvatar: () => void;
  onAvatarUpload: (file?: File) => void;
  onRemoveAvatar: () => void;
  size?: number;
  canEditAvatar?: boolean;
}

const avatarOptionButtonStyles = css`
  align-items: center;
  background: transparent;
  border: none;
  color: #333333;
  cursor: pointer;
  display: flex;
  font-family: "Poppins";
  font-size: 14px;
  gap: 10px;
  padding: 9px 12px;
  text-align: left;
  width: 100%;

  &:hover {
    background: #f5f5f5;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }

  .pi {
    color: #9b9b9b;
    font-size: 20px;
  }
`;

export const ContactAvatarAction = ({
  name,
  avatarUrl,
  avatarLoading,
  onLoadWhatsappAvatar,
  onAvatarUpload,
  onRemoveAvatar,
  size = 43,
  canEditAvatar = true
}: ContactAvatarActionProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const [showAvatarOptions, setShowAvatarOptions] = useState(false);
  const [showAvatarViewer, setShowAvatarViewer] = useState(false);
  const innerSize = size - 6;

  useEffect(() => {
    if (!showAvatarOptions) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!avatarRef.current?.contains(event.target as Node)) {
        setShowAvatarOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAvatarOptions]);

  return (
    <div
      ref={avatarRef}
      css={css`
        position: relative;
      `}
    >
      <div
        className="avatarPreview"
        role={canEditAvatar ? "button" : undefined}
        tabIndex={canEditAvatar ? 0 : undefined}
        onClick={() => {
          if (canEditAvatar) {
            setShowAvatarOptions((value) => !value);
          }
        }}
        onKeyDown={(event) => {
          if (!canEditAvatar) {
            return;
          }
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setShowAvatarOptions((value) => !value);
          }
        }}
        css={css`
          position: relative;
          cursor: ${canEditAvatar ? "pointer" : "default"};
        `}
      >
        {avatarUrl ? (
          <div
            css={css`
              align-items: center;
              border: 1px solid #046af3;
              border-radius: 50%;
              display: flex;
              height: ${size}px;
              justify-content: center;
              margin-right: 5px;
              width: ${size}px;
            `}
          >
            <img
              src={avatarUrl}
              alt="Foto do contato"
              css={css`
                border-radius: 50%;
                height: ${innerSize}px;
                object-fit: cover;
                width: ${innerSize}px;
              `}
            />
          </div>
        ) : (
          <Avatar name={name || "S"} size={size} />
        )}
      </div>

      {canEditAvatar && showAvatarOptions ? (
        <div
          className="avatarOptions"
          css={css`
            background: #ffffff;
            border: 1px solid #e1e6ee;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
            display: flex;
            flex-direction: column;
            left: 0;
            min-width: 180px;
            padding: 6px 0;
            position: absolute;
            top: ${size + 8}px;
            z-index: 10;
          `}
        >
          {avatarUrl ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setShowAvatarOptions(false);
                  setShowAvatarViewer(true);
                }}
                css={avatarOptionButtonStyles}
              >
                <span className="pi pi-eye" />
                <span>Visualizar foto</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowAvatarOptions(false);
                  onRemoveAvatar();
                }}
                css={avatarOptionButtonStyles}
              >
                <span className="pi pi-trash" />
                <span>Excluir foto</span>
              </button>
            </>
          ) : null}

          <button
            type="button"
            disabled={avatarLoading}
            onClick={() => {
              setShowAvatarOptions(false);
              onLoadWhatsappAvatar();
            }}
            css={avatarOptionButtonStyles}
          >
            <img
              src={whatsapp}
              alt=""
              css={css`
                height: 20px;
                width: 20px;
              `}
            />
            <span>{avatarLoading ? "Carregando..." : "Atualizar foto"}</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setShowAvatarOptions(false);
              inputRef.current?.click();
            }}
            css={avatarOptionButtonStyles}
          >
            <span className="pi pi-camera" />
            <span>Carregar foto</span>
          </button>
        </div>
      ) : null}

      <ImageViewer
        images={avatarUrl ? [avatarUrl] : []}
        open={showAvatarViewer}
        onClose={() => setShowAvatarViewer(false)}
      />

      <input
        type="file"
        accept="image/*"
        ref={inputRef}
        onChange={(event) => {
          onAvatarUpload(event.target.files?.[0]);
          event.target.value = "";
        }}
        hidden
      />
    </div>
  );
};

const TopBarChatInfos = ({
  dialog,
  contactName,
  avatarUrl,
  avatarLoading,
  onLoadWhatsappAvatar,
  onAvatarUpload,
  onRemoveAvatar
}: TopBarChatInfosProps) => {
  const name = contactName || dialog.name;
  const canEditAvatar = Boolean(dialog.whatsappQrCode);

  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 84px;
        padding: 13px 20px;
        border-bottom: 1px solid #e5e5e5;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        <div
          css={css`
            border-radius: 50%;
            height: 50px;
            width: 50px;
            margin-right: 10px;
            cursor: ${canEditAvatar ? "pointer" : "default"};
          `}
        >
          <ContactAvatarAction
            name={name}
            avatarUrl={avatarUrl}
            avatarLoading={avatarLoading}
            onLoadWhatsappAvatar={onLoadWhatsappAvatar}
            onAvatarUpload={onAvatarUpload}
            onRemoveAvatar={onRemoveAvatar}
            size={50}
            canEditAvatar={canEditAvatar}
          />
        </div>

        <div>
          <div>{name}</div>

          <div
            css={css`
              font-size: 12px;
              color: rgb(110, 107, 123);
            `}
          >
            Telefone do contato
            <br />
            workspace
          </div>
        </div>
      </div>

      <div
        css={css`
          text-align: center;
          font-size: 12px;
          padding: 5px;
          border-radius: 6px;
          border: 1px solid #e5e5e5;
          color: #046af3;
        `}
      >
        #{dialog.id}
      </div>
    </div>
  );
};

export default TopBarChatInfos;
